import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { Firestore, collection, collectionData, doc, docData, getDoc, setDoc } from '@angular/fire/firestore';
import { getDownloadURL, getStorage, ref, uploadBytesResumable, uploadString } from '@angular/fire/storage';
import { deleteDoc, updateDoc } from 'firebase/firestore';
import { BehaviorSubject, catchError, Observable, of } from 'rxjs';
import { SharedService } from 'src/app/shared/shared.service';

@Injectable({
  providedIn: 'root'
})
export class ProgramBuilderMainService {
	storage = getStorage();
	userId: string = "";
	vttGenerationDetails$ = new BehaviorSubject<any>({
		status: "resolved",
		message: ""
	});

	constructor(
		private firestore: Firestore,
		private httpClient: HttpClient,
		private fns: AngularFireFunctions,
		private sharedService: SharedService
	) {
		this.userId = localStorage.getItem("userId") ?? "";
	}

	addEditCategories(data: any, id:string) {
		// console.log(id);
		// console.log(data);
		let collectionRef = 'categories';
		try{
			if(!id) {
				id = doc(collection(this.firestore, collectionRef)).id;
			}
			collectionRef = `categories/${id}`;
			const categoriesRef = doc(this.firestore, collectionRef);
			if (data.programs) {
				delete data.programs;
			}
			setDoc(categoriesRef, data);
			//   console.log('success')
			return 'success';
		}catch(err) {
			return err;
		}
	}

	getCategories(){
		const categoriesRef = collection(this.firestore, `categories`);
		return collectionData(categoriesRef, {idField: 'id'}) as Observable<any>;
	}

	getCategoriesApi(): Observable<any> {
		let res = this.httpClient.get(`https://admin.videosity.net/server/getCategoriesAPI.php?orgId=87`);
		// console.log(res);
		return res;
	}

	getIgnite(id:any): Observable<any> {
		// console.log('id', id);
		let res = this.httpClient.get(`https://admin.videosity.net/server/getIgnite.php?orgId=87&catId=${id}`);
		// console.log(res);
		return res;
	}

	async addPrograms(id: string, data: any) {
		delete data["isUserEnrolled"];
		if (data['updatedAtDate']) {
			delete data['updatedAtDate'];
		}

		let collectionRef = 'programs';
		try{
			if(!id){
				id = doc(collection(this.firestore, collectionRef)).id;
			}
			data.id = id;
			//   console.log('docId of subject', id)
			//   console.log(data);
			if(data.programImage.includes('data:image/png;base64,') || data.programImage.includes('data:image/jpg;base64,') || data.programImage.includes('data:image/jpeg;base64,')) {
				// console.log(data.programImage)
				data.programImage = await this.sharedService.getUrlForUploadedFile(data.programImage, `programs/${data.id}/images/image.png`)
			}
			if(data.landscapeImage.includes('data:image/png;base64,') || data.landscapeImage.includes('data:image/jpg;base64,') || data.landscapeImage.includes('data:image/jpeg;base64,')) {
				// console.log(data.landscapeImage)
				data.landscapeImage = await this.sharedService.getUrlForUploadedFile(data.landscapeImage, `landscape/${data.id}/images/image.png`)
			}

			// update vttFile url code --------
			for (let sectionIndex = 0; sectionIndex < data.sections.length; sectionIndex++) {
				const section = data.sections[sectionIndex];
				const menus = section.menu;

				if (menus) {
					for (let menuIndex = 0; menuIndex < menus.length; menuIndex++) {
						const menu = menus[menuIndex];
						const topics = menu.metadata.topics;

						if (topics) {
							for (let topicIndex = 0; topicIndex < topics.length; topicIndex++) {
								const topic = topics[topicIndex];
								const vttFile = topic.vttFile;

								if (vttFile && typeof vttFile === 'string' && vttFile.includes('data:text/vtt;base64,')) {
									const url = await this.sharedService.getUrlForUploadedFile(vttFile, `programs/${data.id}/vttFiles/${sectionIndex}/${menuIndex}/${topicIndex}/vttFile.vtt`);
									topic.vttFile = url;
								}
							}
						}
					}
				}

			}
			// update vttFile url code --------

			const programRef = doc(this.firestore, `programs/${id}`);

			// The Program Builder holds its own in-memory copy of the program and
			// only writes it back periodically (autosave) or on explicit actions.
			// Meanwhile, extractAndProcessQuizZip writes quizDeployment/quizUrl
			// directly to this same doc once an uploaded iSpring quiz finishes
			// deploying, asynchronously and independently of the builder page.
			// Blindly overwriting the whole doc with the stale in-memory copy
			// would silently revert a completed deployment back to "processing".
			// Re-fetch and re-apply those backend-owned fields before writing.
			const existingSnap = await getDoc(programRef);
			if (existingSnap.exists()) {
				const existingSections = existingSnap.data()?.['sections'] || [];
				for (const section of data.sections || []) {
					const existingSection = existingSections.find((s: any) => s.sno === section.sno);
					if (!existingSection) continue;

					for (const unit of section.menu || []) {
						if (unit.quizType !== 'ispring quiz') continue;

						const existingUnit = (existingSection.menu || []).find((u: any) => u.sno === unit.sno);
						if (!existingUnit) continue;

						for (const field of ['quizDeployment', 'quizUrl', 'quizSectionIndex', 'quizUnitIndex']) {
							if (existingUnit[field] !== undefined) {
								unit[field] = existingUnit[field];
							}
						}
					}
				}
			}

			await setDoc(programRef, data);
			await this.syncPublishedProgram(id, data);
			// return 'success';
			return { status: "success", data: { ...data, id } };
		}
		catch(err) {
			console.log(err);
			return err
		}
	}

	async deleteCategory(id: string){
		const deleteCategory = doc(this.firestore, `categories/${id}`);
		await deleteDoc(deleteCategory);
		return 'deleted';
	}

	getPrograms(){
		const programsRef = collection(this.firestore, 'programs');
		return collectionData(programsRef, {idField: 'id'}) as Observable<any>;
	}

	async getProgramsAsync(){
		const programsRef = collection(this.firestore, 'programs');
		const data = collectionData(programsRef, {idField: 'id'});
		return await this.sharedService.getObservableRes(data);
	}

	async deleteProgram(id:string){
		const programsRef = doc(this.firestore, `programs/${id}`);
		const publishedRef = doc(this.firestore, `publishedPrograms/${id}`);
		await deleteDoc(publishedRef).catch(() => {});
		await deleteDoc(programsRef);
		return 'deleted'
	}

	async uploadDocumentToStorage(selectedFile: any){
		const filePath = `documents/${selectedFile.name}`;
		const storageRef = ref(this.storage, filePath);
		console.log('storageRef', storageRef)
		try {
			const snapshot = await uploadBytesResumable(storageRef, selectedFile);
			console.log('snapshot'), snapshot
			if (snapshot.state === 'success') {
				const downloadURL = await getDownloadURL(storageRef);
				console.log('downloadURL', downloadURL)
				return downloadURL
			}
		} catch (error) {
			console.error('Error uploading document:', error);
		}
	}

	async uploadVttFile(base64: string, fileName: string, program: any, sectionIndex: any, menuIndex: any, topicIndex: any) {
		try {
			const prgDocRef = doc(this.firestore, "programs", program.id);
			const url = await this.sharedService.getUrlForUploadedFile(base64, `programs/${program.id}/vttFiles/${sectionIndex}/${menuIndex}/${topicIndex}/vttFile.vtt`);

			if(url) {
				program.sections[sectionIndex].menu[menuIndex].metadata.topics[topicIndex].vttFile = url;

				await updateDoc(prgDocRef, { sections: program.sections });
				return url;
			}
		} catch (error) {
			console.error('Error uploading file:', error);
		}
	}

	vttFileGenObs() {
		if (this.userId) {
			return docData(doc(collection(this.firestore, "vttFileGeneration"), this.userId));
		}
	}

	async generateVttFile(videoUrl: string, programId: string, sectionIndex: number, menuIndex: number, topicIndex: number, programData: any) {
		return new Promise(async (resolve, reject) => {
			try {
				const getVideoTranscription = this.fns.httpsCallable('transcript-getVideoTranscription', {
					timeout: 540000
				});
				programData = JSON.stringify(programData);
				const getVideoTranscriptionObs = getVideoTranscription({
					userId: this.userId,
					videoUrl,
					programId,
					sectionIndex,
					menuIndex,
					topicIndex,
					programData
				});
				const getVideoTranscriptionRes: any = await this.sharedService.getObservableRes(getVideoTranscriptionObs);
				// console.log(getVideoTranscriptionRes);
				resolve(getVideoTranscriptionRes);
			} catch (err) {
				reject({
					res: "failed",
					reason: "Call to firebase cloud function failed!"
				});
			}
		});
	}

	async clearGenerationDetails(userId: string) {
		try {
			await deleteDoc(doc(this.firestore, `vttFileGeneration/${userId}`));
		} catch (err) {
			console.log(err);
		}
	}

	async publishProgram(programId: string, programData: any) {
		if (programData['updatedAtDate']) {
			delete programData['updatedAtDate'];
		}
		await this.syncPublishedProgram(programId, programData);
	}

	private async syncPublishedProgram(programId: string, programData: any) {
		const publishedProgramRef = doc(this.firestore, `publishedPrograms/${programId}`);

		if (programData.isPublished !== false) {
			await setDoc(publishedProgramRef, programData);
			return;
		}

		await deleteDoc(publishedProgramRef).catch(() => {});
	}

	getProgramDetails(programId: string): Observable<any> {
		const docRef = doc(this.firestore, `programs/${programId}`);

		return docData(docRef).pipe(
			catchError((error) => {
				console.error('Error fetching real-time program data:', error);
				return of(null);
			})
		);
	}
}
